import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {{ items?: Array<{ product_id: number, quantity: number }>, item_count?: number }} Cart
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} label
 * @property {HTMLElement} liveRegion
 *
 * @extends {Component<Refs>}
 */
class RequestProductButtonComponent extends Component {
  requiredRefs = ['button', 'label', 'liveRegion'];
  #isLoading = false;
  #abortController = new AbortController();
  /** @type {number | undefined} */
  #alreadyInCartTimeout;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });

    this.#syncButtonState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.#isLoading || !this.variantId || !this.productId || this.refs.button.disabled) return;

    this.#setLoading(true);

    try {
      const cart = await this.#fetchCart();
      const currentQuantity = this.#getProductQuantity(cart);
      const cartLimitStr = this.dataset.cartLimit;
      const cartLimit = cartLimitStr ? parseInt(cartLimitStr, 10) : null;

      if (cartLimit !== null && currentQuantity >= cartLimit) {
        // Respect the cart restriction, but keep the button clickable.
        const alreadyInCartMessage =
          this.dataset.alreadyInCartLabel || this.dataset.addedLabel || this.dataset.defaultLabel || '';
        this.#flashMessage(alreadyInCartMessage);
        this.#applyCartState(cart);
        return;
      }

      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');
      formData.append('sections_url', window.location.pathname);

      const sectionIds = this.#getSectionIds();
      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });
      const parsedResponse = await response.json();

      if (parsedResponse.status) {
        this.#announce(parsedResponse.message || this.dataset.errorLabel || 'Unable to add item to cart.');
        this.dispatchEvent(
          new CartErrorEvent(this.id || this.variantId, parsedResponse.message, parsedResponse.description, parsedResponse.errors)
        );
        return;
      }

      const updatedCart = await this.#fetchCart();
      const addedMessage = this.dataset.addedLabel || this.dataset.defaultLabel || '';
      this.#flashMessage(addedMessage);
      this.#applyCartState(updatedCart);

      this.dispatchEvent(
        new CartAddEvent(updatedCart, this.id || this.variantId, {
          source: 'request-product-button-component',
          itemCount: updatedCart.item_count || 0,
          productId: this.productId,
          variantId: this.variantId,
          sections: parsedResponse.sections,
        })
      );
    } catch (error) {
      console.error('Request product button failed:', error);
      this.#announce(this.dataset.errorLabel || 'Unable to add item to cart.');
    } finally {
      this.#setLoading(false);
      await this.#syncButtonState();
    }
  };

  /**
   * @param {CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event.detail?.resource?.items) {
      this.#applyCartState(event.detail.resource);
      return;
    }

    await this.#syncButtonState();
  };

  async #syncButtonState() {
    if (!this.productId) {
      this.#setUnavailableState();
      return;
    }

    try {
      const cart = await this.#fetchCart();
      this.#applyCartState(cart);
    } catch (error) {
      console.error('Unable to sync request product button:', error);
    }
  }

  /**
   * @param {Cart} cart
   */
  #applyCartState(cart) {
    const isAvailable = this.dataset.available === 'true';
    const hasProduct = this.dataset.hasProduct === 'true';
    // Keep clickable when the item is already in cart; we enforce max=1 on click.
    const shouldDisable = !hasProduct || !isAvailable;

    this.refs.button.disabled = shouldDisable || this.#isLoading;

    if (this.#alreadyInCartTimeout) return;

    if (!hasProduct) {
      this.#updateLabelText(this.dataset.missingProductLabel || this.dataset.defaultLabel || '');
      return;
    }

    if (!isAvailable) {
      this.#updateLabelText(this.dataset.unavailableLabel || this.dataset.defaultLabel || '');
      return;
    }

    this.#updateLabelText(this.dataset.defaultLabel || '');
  }

  #setUnavailableState() {
    this.refs.button.disabled = true;
    if (this.#alreadyInCartTimeout) return;
    this.#updateLabelText(
      this.dataset.hasProduct === 'true'
        ? this.dataset.unavailableLabel || this.dataset.defaultLabel || ''
        : this.dataset.missingProductLabel || this.dataset.defaultLabel || ''
    );
  }

  /**
   * @param {Cart} cart
   * @returns {number}
   */
  #getProductQuantity(cart) {
    if (!this.productId || !cart.items) return 0;

    return cart.items.reduce((total, item) => {
      return item.product_id?.toString() === this.productId ? total + item.quantity : total;
    }, 0);
  }

  /**
   * @returns {Promise<Cart>}
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`);
    return response.json();
  }

  /**
   * @returns {string[]}
   */
  #getSectionIds() {
    const sections = new Set();

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.add(item.dataset.sectionId);
      }
    });

    return Array.from(sections);
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    this.#isLoading = isLoading;
    this.refs.button.toggleAttribute('aria-busy', isLoading);
  }

  /**
   * Temporarily replace the label with a message, then revert to the normal state.
   * @param {string} message
   */
  #flashMessage(message) {
    if (this.#alreadyInCartTimeout) {
      clearTimeout(this.#alreadyInCartTimeout);
      this.#alreadyInCartTimeout = undefined;
    }

    if (message) {
      this.#updateLabelText(message);
      this.#announce(message);
    }

    // Revert after a short delay to the regular label derived from cart state.
    this.#alreadyInCartTimeout = window.setTimeout(() => {
      this.#alreadyInCartTimeout = undefined;
      this.#syncButtonState();
    }, 3000);
  }

  /**
   * Animates text change smoothly
   * @param {string} newText
   */
  #updateLabelText(newText) {
    if (this.refs.label.textContent === newText) return;

    this.refs.label.style.transition = 'opacity 0.2s ease-in-out';
    this.refs.label.style.opacity = '0';

    setTimeout(() => {
      this.refs.label.textContent = newText;
      this.refs.label.style.opacity = '1';
    }, 200);
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    this.refs.liveRegion.textContent = message;
  }

  get productId() {
    return this.dataset.productId || '';
  }

  get variantId() {
    return this.dataset.variantId || '';
  }
}

if (!customElements.get('request-product-button-component')) {
  customElements.define('request-product-button-component', RequestProductButtonComponent);
}
